import { Button } from "primereact/button";
import { Column } from "primereact/column";
import {
  DataTable,
  DataTablePageEvent,
  DataTableSortEvent,
} from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import React, { useEffect, useMemo, useState } from "react";
import { FilterMatchMode, FilterService } from "primereact/api";
import { Calendar } from "primereact/calendar";
import { MultiSelect } from "primereact/multiselect";
import { useTranslation } from "react-i18next";
import "./styled.css"; // Importando o CSS para estilização

//#region  Column Config

export interface ColumnConfig {
  field: string;
  header: string;
  body?: (rowData: any) => React.ReactNode;
  sortField?: string; // campo usado para ordenação, se diferente do field
  style?: React.CSSProperties;
  filterType?: "date" | "multiSelect" | "text" | "numeric";
  filterOptions?: { label: string; value: any }[];
}

interface GenericTableProps {
  data: any[];
  columns: ColumnConfig[];
  loading?: boolean;
  onEdit?: (rowData: any) => void;
  onDelete?: (rowData: any) => void;
  totalRecords: number;
  first: number;
  rows: number;
  sortField?: string;
  sortOrder?: 1 | -1;
  /** dispara sempre que qualquer filtro (ou ordenação/paginação) mudar */
  onFilter?: (filters: Record<string, any>) => void;
  onPage?: (event: DataTablePageEvent) => void;
  onSorts?: (event: DataTableSortEvent) => void;
  extraHeaderContent?: React.ReactNode;
}

const STORAGE_KEY = "TableFilterAdvanced_filters";

const TableFilterAdvanced: React.FC<GenericTableProps> = ({
  data,
  columns,
  loading = false,
  onFilter,
  totalRecords,
  first,
  sortField,
  sortOrder,
  rows,
  onPage,
  onSorts,
  extraHeaderContent,
}) => {
  //#region  Filters

  // Criar filtros dinamicamente baseados nas colunas
  const createFilters = (): Record<string, any> => {
    const filters: Record<string, any> = {
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    };

    columns.forEach((col) => {
      if (col.filterType === "date") {
        filters[col.field] = { value: null, matchMode: "customDateFilter" };
      } else if (col.filterType === "multiSelect") {
        filters[col.field] = { value: [], matchMode: FilterMatchMode.IN };
      } else if (col.filterType === "numeric") {
        filters[col.field] = { value: null, matchMode: FilterMatchMode.EQUALS };
      } else if (col.filterType === "text") {
        filters[col.field] = { value: null, matchMode: FilterMatchMode.CONTAINS };
      } else {
        filters[col.field] = { value: null, matchMode: null };
      }
    });

    return filters;
  };

  //persistir os filtros no localStorage
  // const [filters, setFilters] = useState<Record<string, any>>(createFilters());
  const [filters, setFilters] = useState<Record<string, any>>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : createFilters();
  });
  const { t: translateForm } = useTranslation("form");

  // Determina se existem filtros ativos
  const hasFilters = useMemo(() => {
    return Object.entries(filters).some(([, filter]) => {
      if (Array.isArray(filter.value)) {
        return filter.value.length > 0;
      }
      return filter.value !== null && filter.value !== "";
    });
  }, [filters]);

  // register custom date filter once
  // effect para disparar fetch inicial com filtros persistidos
  useEffect(() => {
    if (onFilter) {
      onFilter(filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    FilterService.register("customDateFilter", (value, filter) => {
      if (value && filter) {
        return (
          new Date(value).toLocaleDateString("pt-BR") ===
          new Date(filter).toLocaleDateString("pt-BR")
        );
      }
      return false;
    });
  }, []);

  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "draggable"
        ) {
          const target = mutation.target as HTMLElement;
          if (target.getAttribute("draggable") === "true") {
            target.setAttribute("draggable", "false");
          }
        }
      });
    });

    const tableCells = document.querySelectorAll(
      ".p-datatable .p-datatable-tbody td",
    );

    tableCells.forEach((td) => {
      td.setAttribute("draggable", "false");

      observer.observe(td, {
        attributes: true,
        attributeFilter: ["draggable"],
      });

      td.querySelectorAll("*").forEach((child) => {
        (child as HTMLElement).setAttribute("draggable", "false");
        observer.observe(child, {
          attributes: true,
          attributeFilter: ["draggable"],
        });
      });
    });

    return () => {
      observer.disconnect();
    };
  }, [data]);

  // Função helper para atualizar filtros, notificar pai e persistir
  const updateFilters: any = (newFilters: Record<string, any>) => {
    setFilters(newFilters);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newFilters));
    onFilter?.(newFilters);
  };

  const clearFilter: any = () => {
    const f = createFilters();
    setFilters(f);
    localStorage.removeItem(STORAGE_KEY);
    onFilter?.(f);
  };

  // templante numericFilterTemplate
  const numericFilterTemplate: any = (opts: any) => (
    <InputText
      type="number"
      value={filters[opts.field]?.value ?? ""}
      onChange={(e) => {
        const val = e.target.value;
        const newFilters = {
          ...filters,
          [opts.field]: {
            value: val !== "" ? Number(val) : null,
            matchMode: opts.filterMatchMode,
          },
        };
        updateFilters(newFilters);
      }}
      placeholder={translateForm("Filter")}
      className="p-column-filter"
    />
  );

  // Template para filtro de texto
  const textFilterTemplate: any = (opts: any) => (
    <InputText
      value={filters[opts.field]?.value || ""}
      onChange={(e) => {
        const newFilters = {
          ...filters,
          [opts.field]: {
            value: e.target.value,
            matchMode: opts.filterMatchMode,
          },
        };
        updateFilters(newFilters);
      }}
      placeholder={translateForm("Filter")}
      className="p-column-filter"
    />
  );

  const dateFilterTemplate = (opts: any): JSX.Element => (
    <Calendar
      value={opts.value ? new Date(opts.value) : null}
      onChange={(e) => {
        const newFilters = {
          ...filters,
          [opts.field]: {
            value: e.target.value,
            matchMode: opts.filterMatchMode,
          },
        };
        updateFilters(newFilters);
      }}
      dateFormat="dd/mm/yy"
      placeholder="Selecione a data"
      showIcon
    />
  );

  const multiSelectFilterTemplate: any = (
    options: any,
    column: ColumnConfig,
  ) => (
      <MultiSelect
      value={filters[options.field]?.value || []}
      options={column.filterOptions!}
      onChange={(e) => {
        const newFilters = {
          ...filters,
          [options.field]: {
            value: e.value,
            matchMode: options.filterMatchMode,
          },
        };
        updateFilters(newFilters);
      }}
      placeholder={translateForm("Select")}
      className="p-column-filter"
      optionLabel="label"
      optionValue="value"
      display="comma"
      style={{ width: "100%", maxWidth: "200px" }}
    />
  );

  //     #region  DataTable
  return (
    <div>
      <div className="flex gap-4 mb-3">
        <Button
          type="button"
          icon="pi pi-filter-slash"
          label={translateForm("Clear")}
          outlined={hasFilters}
          severity="success"
          onClick={clearFilter}
          className="gap-2 btn"
        />
        <div className="flex align-items-center gap-2">
          {extraHeaderContent}
        </div>
      </div>
      <DataTable
        value={data}
        paginator
        size="small"
        paginatorTemplate="RowsPerPageDropdown PrevPageLink PageLinks NextPageLink"
        rowsPerPageOptions={[10, 20, 30, 40, 50]}
        first={first}
        rows={rows}
        //       //redimensionar colunas
        //        resizableColumns
        // columnResizeMode="fit" // ou "expand"
        loading={loading}
        lazy // 📌 ativa lazy loading
        totalRecords={totalRecords}
        filters={filters}
        filterDisplay="row"
        onFilter={(e) => {
          const newFilters = e.filters;
          setFilters(newFilters);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(newFilters));
          onFilter?.(newFilters);
          onPage?.({ first: 0, rows } as DataTablePageEvent);
        }}
        onPage={(e) => onPage?.(e)} // dispara paginação server-side
        onSort={(e) => {
          onSorts?.(e);
        }}
        sortField={sortField}
        sortOrder={sortOrder}
      >
        {columns.map((col) => {
          // desabilita filtro apenas nas colunas específicas
          const disableFilter = [
            // "operationtype",
            "actions",
            // "isActive",
            "isProducible",
            // "status",
          ].includes(col.field);
          const disableSort = ["actions"].includes(col.field); // <- coluna actions não deve ordenar
          return (
            <Column
              key={col.field}
              field={col.field}
              sortField={col.sortField || col.field}
              sortable={!disableSort} // <- aqui passa false para actions
              header={col.header}
              body={col.body}
              style={col.style}
              // habilita filtro em todas as colunas exceto as desabilitadas
              filter={!disableFilter}
              filterMatchMode={filters[col.field]?.matchMode}
              filterElement={
                disableFilter
                  ? undefined
                  : (opts: any) => {
                      if (col.filterType === "date")
                        return dateFilterTemplate(opts);
                      if (col.filterType === "multiSelect")
                        return multiSelectFilterTemplate(opts, col);
                      if (col.filterType === "numeric")
                        return numericFilterTemplate(opts, col);
                      else return textFilterTemplate(opts);
                    }
              }
            />
          );
        })}
      </DataTable>
    </div>
  );
};

export default TableFilterAdvanced;
